import * as React from 'react';
import _ from 'lodash';

import { useMutation, useQuery } from "@apollo/client";
import { useRouteMatch } from "react-router";
import { CREATE_WHEEL_FORTUNE_EVENT, GET_ALL_WHEEL_FORTUNE_EVENTS } from "./graphql";
import { 
  CreateWfEventInput,
  CreateWheelFortuneEventMutation,
  CreateWheelFortuneEventMutationVariables,
  GetAllWheelFortuneEventsQuery,
  WheelFortuneEventConfig
} from "../../../../../__gqltypes__";
import { WheelFortuneEventData } from './components/WheelFortuneEventData';
import { EventTabContainer } from '../../general/EventTabContainer';

const baseURL = '/sp3/liveops/wheel-fortune';

type WFConfig = Pick<WheelFortuneEventConfig, 'id'  | 'name' | 'startDate' | 'stopDate' | 'status'>;

function WheelFortune(): JSX.Element {
    
    const match = useRouteMatch();
    const skip = React.useMemo(() => {
      return !match.path.includes(baseURL);
    }, [match.path]);
    const [wheelFortuneEvents, setWheelFortuneEvents] = React.useState<Array<WFConfig>>([]);
    
    const { data: wheelFortuneData, loading: eventsLoading } = useQuery<GetAllWheelFortuneEventsQuery>(
      GET_ALL_WHEEL_FORTUNE_EVENTS,
      {
        skip,
        fetchPolicy: 'no-cache',
      }
    );

    const [createWF, { loading: createWFLoading}] = useMutation<
      CreateWheelFortuneEventMutation, 
      CreateWheelFortuneEventMutationVariables
      >(CREATE_WHEEL_FORTUNE_EVENT);

    const handleUpsert = React.useCallback((offer: WheelFortuneEventConfig) => {
      const other = wheelFortuneEvents.filter(({id}) => id !== offer.id);
      const all = [...other, offer];
      setWheelFortuneEvents(_.orderBy(all, 'startDate', 'desc'));
    }, [wheelFortuneEvents, setWheelFortuneEvents]);

    const handleAddWheelFortune = React.useCallback((wfInput: CreateWfEventInput) => {
      return new Promise<string | null>((resolve, reject) => {
        createWF({
          variables: {
            input: wfInput
          }
        }).then(({data}) => {
          const newEvent = data?.createWheelFortuneEvent?.event;
          if (newEvent) {
            handleUpsert(newEvent);
            resolve(newEvent.id);
          } else {
            resolve(null);
          }
    
        }).catch((error) => {
          console.error(error);
          reject(error);
        });
      });
    }, [createWF, handleUpsert]);

    const handleRemoveWheelFortune = React.useCallback((chainedOfferId: string) => {
      const leftChainedOffer = wheelFortuneEvents.filter(({id}) => id !== chainedOfferId);
      setWheelFortuneEvents(leftChainedOffer);
    }, [wheelFortuneEvents, setWheelFortuneEvents]);

    React.useEffect(() => {
      if (wheelFortuneData && wheelFortuneData.liveOps) {
        const _wheelFortuneData = _.orderBy(wheelFortuneData.liveOps.getAllWheelFortuneEvents, 'start', 'desc');
        setWheelFortuneEvents(_.compact(_wheelFortuneData));
      }
    }, [wheelFortuneData]);

    const eventComponent = React.useCallback((eventId: string) => {
      return  <WheelFortuneEventData
                eventId={eventId}
                onUpserEvent={handleUpsert}
                onRemoveEvent={handleRemoveWheelFortune}/>;
    }, [handleRemoveWheelFortune, handleUpsert]);

    const loading = eventsLoading || createWFLoading;
    return <EventTabContainer 
      title="Wheel Fortune"
      baseURL={baseURL}
      events={wheelFortuneEvents}
      eventsLoading={eventsLoading}
      loading={loading}
      onAddEvent={handleAddWheelFortune}
      eventComponent={eventComponent}
      />;
}

export default WheelFortune;